import { useState, useEffect, FC } from 'react';
import { Link as _Link, useLocation } from 'react-router-dom';
import {
  Grid,
  Tabs as _Tabs,
  Tab as _Tab,
  Table,
  TableHead as _TableHead,
  TableBody,
  TableRow as _TableRow,
  TableCell as _TableCell,
  LinearProgress,
} from '@material-ui/core';
import styled from '@emotion/styled';
import { useQuery, useReactiveVar } from '@apollo/client';

import { lastAccessedOrganizationVar } from '../../cache';
import { GetProjectsDocument, GetProjectsQuery, GetProjectsQueryVariables, Project } from '../../generated/graphql';
import {
  PageTitle,
  SectionTitle,
  SectionSubTitleStyle,
  DescriptionBoldStyle,
  Link,
} from '../component/primitive/TypographyUI';
import { SPACING_MEDIUM } from '../constant/spacing';
import { COLOR_TEXT_MAIN } from '../../theme/colors';
import { PaperContainer } from '../component/primitive/Paper';
import Button from '../component/primitive/Button';
import LoadingButton from '../component/primitive/LoadingButton';

const roundStates: { [key: string]: string } = {
  initial: '準備中',
  competition: 'ラウンド中',
  interim: 'インターバル中',
  archived: '終了',
};

const SitedockListPage: FC = () => {
  const { search } = useLocation();
  const [tab, setTab] = useState(0);
  const [projects, setProjects] = useState<Array<Project>>();
  const [pagination, setPagination] = useState(1);
  const { id: organization_id } = useReactiveVar(lastAccessedOrganizationVar)!;

  const { loading, error } = useQuery<GetProjectsQuery, GetProjectsQueryVariables>(GetProjectsDocument, {
    variables: {
      organization_id: organization_id,
      search_string: `testing_experience=except&order=created_at_desc&per_page=50&page=${pagination}`,
    },
    context: { clientName: 'planbcd' },
    skip: !organization_id,
    onCompleted: ({ projects: loadedProjects }) => {
      setProjects([...(projects ? projects : []), ...loadedProjects]);
    },
  });

  const handleTab = (_: React.ChangeEvent<Record<string, never>>, newValue: number) => {
    setTab(newValue);
  };
  const onClickLoadMore = () => {
    setPagination(pagination + 1);
  };

  useEffect(() => setProjects([]), [organization_id]);

  return (
    <Root>
      <TitleFlex>
        <PageTitle variant="h2">サイト診断</PageTitle>
        <Button variant="outlined" size="small">
          <_Link to="/engine/sitedock">旧Sitedockはこちら</_Link>
        </Button>
      </TitleFlex>
      <Tabs value={tab} onChange={handleTab} aria-label="Sitedock tabs" indicatorColor="primary" textColor="inherit">
        <Tab label="エクスペリエンス" />
      </Tabs>
      {loading && <LinearProgress />}
      <PaperContainer>
        <SectionTitle variant="h3">エクスペリエンス一覧</SectionTitle>
        {projects && projects.length !== 0 && (
          <ProjectTable aria-label="table">
            <TableHead>
              <TableRow>
                <TableCellId>ID</TableCellId>
                <TableCell>タイトル</TableCell>
                <TableCellRound>ラウンド</TableCellRound>
              </TableRow>
            </TableHead>
            <TableBody>
              {projects!.map((project: any /*Project*/) => (
                <TableRow key={project.id}>
                  <TableCellId>{project.id}</TableCellId>
                  <TableCell>
                    <Link to={project.id + search}>{project.title}</Link>
                  </TableCell>
                  <TableCellRound align="right">
                    {project.state in roundStates && roundStates[project.state]}
                  </TableCellRound>
                </TableRow>
              ))}
              {projects.length % 50 === 0 && (
                <Grid container justifyContent="center">
                  <SecondaryButton onClick={onClickLoadMore} loading={loading}>
                    さらに読み込む
                  </SecondaryButton>
                </Grid>
              )}
            </TableBody>
          </ProjectTable>
        )}
        {((!loading && projects && projects.length === 0) || error) && (
          <Grid container justifyContent="center">
            <NoProjects item>エクスペリエンスがありません</NoProjects>
          </Grid>
        )}
      </PaperContainer>
    </Root>
  );
};

const Root = styled('div')({
  padding: '0 24px',
});

const TitleFlex = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
});

const Tabs = styled(_Tabs)({
  marginBottom: SPACING_MEDIUM,
  '& .MuiTabs-indicator': {
    backgroundColor: COLOR_TEXT_MAIN,
    height: '3px',
    borderRadius: '999px',
  },
});
const Tab = styled(_Tab)({
  ...SectionSubTitleStyle,
  ...{ '&.MuiTab-root': { padding: 0 } },
});

const ProjectTable = styled(Table)({
  marginTop: SPACING_MEDIUM,
});
const TableHead = styled(_TableHead)({
  background: 'none',
});

const TableRow = styled(_TableRow)({
  minHeight: 48,
  verticalAlign: 'middle',
  display: 'flex',
});

const TableCellBase = styled(_TableCell)({
  '&.MuiTableCell-head': { ...DescriptionBoldStyle },
  display: 'flex',
  alignItems: 'center',
  color: COLOR_TEXT_MAIN,
});
const TableCell = styled(TableCellBase)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  flex: '1 1 90%',
});
const TableCellId = styled(TableCellBase)({
  flex: '0 0 110px',
});
const TableCellRound = styled(TableCellBase)({
  flex: '0 1 190px',
});

const SecondaryButton = styled(LoadingButton)(`
  color: ${COLOR_TEXT_MAIN};
  margin-top: 1rem;
  height: 40px;
  font-size: 14px;
  background: #FFFFFF;
  font-weight: 700;
  border: 1.5px solid #E6E7E8;
  border-radius: 4px;
  padding: 12px 24px;
  white-space: nowrap;
  :hover {
    background: #FFFFFF;
    color: #707889;
  }
`);

const NoProjects = styled(Grid)(`margin: 1rem`);

export default SitedockListPage;
